import React, { useMemo } from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { PatternBuilderCallbacks } from 'apps/sigma2-ui/src/components/permissions/PatternBuilder';
import { PatternSegmentRemoveButton } from 'apps/sigma2-ui/src/components/permissions/PatternBuilder';
import { PatternSegmentEditButton } from 'apps/sigma2-ui/src/components/permissions/PatternBuilder';
import { PatternSegmentReadOnly } from 'apps/sigma2-ui/src/components/permissions/PatternBuilder';
import { DataRule } from 'libs/end-to-end/src/data-rules';
import { DataRulePattern } from 'libs/end-to-end/src/data-rules';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { PatternBuilder } from 'apps/sigma2-ui/src/components/permissions/PatternBuilder';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { EntitlementFeature } from 'libs/end-to-end/src/group-entitlements';
import { isEntitlementFeatureDeprecated } from 'libs/end-to-end/src/group-entitlements';
import { EntitlementFeatureKey } from 'libs/end-to-end/src/group-entitlements';
import { EntitlementFeatureKeys } from 'libs/end-to-end/src/group-entitlements';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { FocusAreas } from 'libs/end-to-end/src/focus-area';
import { isEntitlementFeatureWithOptionalFocusArea } from 'libs/end-to-end/src/group-entitlements';
import { validateEntitlementFeatures } from 'libs/end-to-end/src/group-entitlements';
import cn from 'classnames';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';

export interface GroupEntitlementCallbacks {
  onApplyEntitlementChanges: () => void;
  onDiscardEntitlementChanges: () => void;
  onAddDataRule: () => void;
  onAddFeature: () => void;
  onChangePendingFeatureKey: (newValue: EntitlementFeatureKey) => void;
  onChangePendingFeatureFocusArea: (newValue: FocusArea | null) => void;
  onEditEntitlement: (index: number) => void;
  onEditDataRule: (index: number, part: 'header' | 'value') => void;
  onDeleteDataRule: (index: number) => void;
  onApplyDataRuleChanges: () => void;
  onDiscardDataRuleChanges: () => void;
  onDeleteEntitlement: (index: number) => void;
  onDeleteFeature: (index: number) => void;
}

export interface GroupEntitlementRowProps {
  databaseName: string;
  entitlementIndex: number;
  dataRules: DataRule[];
  features: EntitlementFeature[];
  pendingFeature: EntitlementFeature;
  showAddDataRuleButton: boolean;
  showAddFeatureButton: boolean;
  showEditEntitlementButton: boolean;
  showDeleteEntitlementButton: boolean;
  showEntitlementChangeButtons: boolean;
  showDeleteFeatureButton: boolean;
  groupEntitlementCallbacks: GroupEntitlementCallbacks;
  currentlyEditingDataRule: {
    index: number;
    part: 'header' | 'value';
  } | null;
  showEditDataRuleButton: boolean;
  canApplyDraftDataRuleChanges: boolean;
  canApplyEntitlementChanges: boolean;
  showDeleteDataRuleButton: boolean;
  pendingSegmentIndex: number;
  patternBuilderCallbacks: PatternBuilderCallbacks;
}

export interface PatternReadOnlyProps {
  pattern: DataRulePattern;
}

export const PatternReadOnly: React.FC<PatternReadOnlyProps> = ({
  pattern,
}) => {
  const language = useLanguage();

  if (pattern.length === 0) {
    return (
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.noPatternSelected()}
      />
    );
  }

  return (
    <>
      <S2Typography
        as={'span'}
        variant="headline-7-alt"
        translatedContent={language.patternLabelWithoutDotDotDot()}
      />
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.space()}
      />
      {pattern.map((segment, index) => {
        return (
          <PatternSegmentReadOnly
            key={index}
            segment={segment}
            segmentNumber={index + 1}
          />
        );
      })}
      <S2Typography
        as={'span'}
        variant="headline-7-alt"
        translatedContent={language.endPatternLabel()}
      />
    </>
  );
};

export const GroupEntitlementRow: React.FC<GroupEntitlementRowProps> = ({
  entitlementIndex,
  databaseName,
  dataRules,
  features,
  pendingFeature,
  showAddDataRuleButton,
  showAddFeatureButton,
  showEditEntitlementButton,
  showDeleteEntitlementButton,
  currentlyEditingDataRule,
  showEditDataRuleButton,
  showDeleteDataRuleButton,
  showDeleteFeatureButton,
  canApplyDraftDataRuleChanges,
  showEntitlementChangeButtons,
  canApplyEntitlementChanges,
  pendingSegmentIndex,
  groupEntitlementCallbacks,
  patternBuilderCallbacks,
}) => {
  const language = useLanguage();

  const GroupEntitlementTableColumnPart: React.FC<{
    isCurrentlyEditingPart: boolean;
    partPattern: DataRulePattern;
    part: 'header' | 'value';
    index: number;
  }> = ({ isCurrentlyEditingPart, partPattern, part, index }) => {
    return (
      <div className={'leading-[3.5rem]'}>
        {!isCurrentlyEditingPart && <PatternReadOnly pattern={partPattern} />}
        {isCurrentlyEditingPart && (
          <Spacing size={'md'} direction={'vertical'}>
            <PatternBuilder
              pattern={partPattern}
              pendingSegmentIndex={pendingSegmentIndex}
              patternBuilderCallbacks={patternBuilderCallbacks}
            />
            {pendingSegmentIndex === -1 && (
              <Spacing direction={'horizontal'} size={'md'}>
                <Button
                  variant={'primary'}
                  disabled={!canApplyDraftDataRuleChanges}
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      groupEntitlementCallbacks.onApplyDataRuleChanges();
                    },
                  }}
                >
                  {convertTranslatedContentToReactNode(language.ok())}
                </Button>
                <Button
                  variant={'destructive'}
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      groupEntitlementCallbacks.onDiscardDataRuleChanges();
                    },
                  }}
                >
                  {convertTranslatedContentToReactNode(language.cancel())}
                </Button>
              </Spacing>
            )}
          </Spacing>
        )}
        {showEditDataRuleButton &&
          (currentlyEditingDataRule === null ||
            (currentlyEditingDataRule.index !== index &&
              currentlyEditingDataRule.part !== part)) && (
            <PatternSegmentEditButton
              onClick={() => {
                groupEntitlementCallbacks.onEditDataRule(index, part);
              }}
            />
          )}
      </div>
    );
  };

  const featureValidationResult = useMemo(() => {
    return validateEntitlementFeatures(features);
  }, [features]);

  return (
    <div
      className={
        'flex flex-col gap-4 rounded-lg bg-gray-100 border-2 border-gray-300 p-4'
      }
    >
      <div className={'flex flex-row gap-2 justify-between'}>
        <S2Typography
          as={'span'}
          variant={'body-6'}
          translatedContent={language.entitlementDatabaseNameLabel({
            databaseName,
          })}
        />
        <Spacing direction={'horizontal'} size={'md'}>
          {showEntitlementChangeButtons && (
            <>
              <Button
                variant={'primary'}
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    groupEntitlementCallbacks.onApplyEntitlementChanges();
                  },
                }}
                disabled={!canApplyEntitlementChanges}
              >
                {convertTranslatedContentToReactNode(language.ok())}
              </Button>
              <Button
                variant={'destructive'}
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    groupEntitlementCallbacks.onDiscardEntitlementChanges();
                  },
                }}
                disabled={false}
              >
                {convertTranslatedContentToReactNode(language.cancel())}
              </Button>
            </>
          )}
          {showDeleteEntitlementButton && (
            <Button
              variant={'destructive'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  groupEntitlementCallbacks.onDeleteEntitlement(
                    entitlementIndex,
                  );
                },
              }}
              disabled={false}
            >
              {convertTranslatedContentToReactNode(
                language.deleteEntitlementButton(),
              )}
            </Button>
          )}
          {showEditEntitlementButton && (
            <Button
              variant={'outline'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  groupEntitlementCallbacks.onEditEntitlement(entitlementIndex);
                },
              }}
              disabled={false}
            >
              {convertTranslatedContentToReactNode(
                language.editEntitlementButton(),
              )}
            </Button>
          )}
        </Spacing>
      </div>
      <div className={'flex flex-row justify-between items-end'}>
        <S2Typography
          as={'span'}
          variant={'headline-5-alt'}
          translatedContent={language.featuresLabel()}
        />
        {showAddFeatureButton && (
          <Spacing size={'md'} direction={'horizontal'}>
            <SelectBoxWithSearch
              type={'single'}
              setSelected={(s) => {
                groupEntitlementCallbacks.onChangePendingFeatureKey(s);
              }}
              selected={pendingFeature.key}
              list={EntitlementFeatureKeys.filter(
                (k) => !isEntitlementFeatureDeprecated(k),
              ).map((k) => {
                return {
                  optionLabel: k,
                  optionValue: k,
                };
              })}
              label={getTranslatedContentAsString(
                language.selectFeatureLabel(),
              )}
            />
            {isEntitlementFeatureWithOptionalFocusArea(pendingFeature.key) && (
              <SelectBoxWithSearch
                type={'single'}
                setSelected={(s) => {
                  groupEntitlementCallbacks.onChangePendingFeatureFocusArea(
                    s === 'none' ? null : s,
                  );
                }}
                selected={
                  pendingFeature.focusArea === null
                    ? 'none'
                    : pendingFeature.focusArea
                }
                list={[
                  { optionLabel: 'All', optionValue: 'none' },
                  ...FocusAreas.map((focusArea) => {
                    return {
                      optionLabel: focusArea,
                      optionValue: focusArea,
                    };
                  }),
                ]}
                label={getTranslatedContentAsString(
                  language.selectFocusAreaLabel(),
                )}
              />
            )}
            <Button
              variant={'outline'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  groupEntitlementCallbacks.onAddFeature();
                },
              }}
              disabled={false}
            >
              {convertTranslatedContentToReactNode(
                language.addNewFeatureButton(),
              )}
            </Button>
          </Spacing>
        )}
      </div>
      {features.length > 0 && (
        <table className={'w-full border-gray-600 border-2 bg-white'}>
          <thead>
            <tr>
              <th
                className={
                  'bg-gray-200 border-gray-600 border-2 text-center px-8 py-4'
                }
                style={{
                  width: showDeleteFeatureButton ? '45%' : '50%',
                }}
              >
                <S2Typography
                  as={'span'}
                  variant={'headline-7-alt'}
                  translatedContent={language.featureKeyTableHeading()}
                />
              </th>
              <th
                className={
                  'bg-gray-200 border-gray-600 border-2 text-center px-8 py-4'
                }
                style={{
                  width: showDeleteFeatureButton ? '45%' : '50%',
                }}
              >
                <S2Typography
                  as={'span'}
                  variant={'headline-7-alt'}
                  translatedContent={language.featureFocusAreaTableHeading()}
                />
              </th>
              {showDeleteFeatureButton && (
                <th
                  className={'bg-gray-200 border-gray-600 border-2'}
                  style={{
                    width: '10%',
                  }}
                >
                  &nbsp;
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {features.map((feature, index) => {
              return (
                <tr
                  key={index}
                  className={cn({
                    'bg-red-200':
                      featureValidationResult.resultType === 'err' &&
                      featureValidationResult.error.errorType ===
                        'ValidateEntitlementFeaturesDuplicateError' &&
                      featureValidationResult.error.invalidIndexes.includes(
                        index,
                      ),
                  })}
                >
                  <td
                    className={'p-2 align-top border-gray-600 border-2'}
                    style={{
                      width: '40%',
                    }}
                  >
                    <S2Typography
                      as={'span'}
                      variant={'headline-7-alt'}
                      translatedContent={language.featureKey({
                        key: feature.key,
                      })}
                    />
                  </td>
                  <td
                    className={'p-2 align-top border-gray-600 border-2'}
                    style={{
                      width: '40%',
                    }}
                  >
                    <S2Typography
                      as={'span'}
                      variant={'headline-7-alt'}
                      translatedContent={language.featureFocusArea({
                        key: feature.key,
                        focusArea: feature.focusArea,
                      })}
                    />
                  </td>
                  {showDeleteFeatureButton && (
                    <td
                      className={
                        'p-2 align-top text-center border-gray-600 border-2'
                      }
                      style={{
                        width: '10%',
                      }}
                    >
                      <PatternSegmentRemoveButton
                        onRemove={() => {
                          groupEntitlementCallbacks.onDeleteFeature(index);
                        }}
                        tooltipTranslatedContent={language.removeFeatureTooltip()}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {featureValidationResult.resultType === 'err' && (
        <S2Typography
          as={'span'}
          variant={'headline-7-alt'}
          translatedContent={language.featureValidationError()}
          textColorVariant={'red-600'}
        />
      )}
      {features.length === 0 && (
        <S2Typography
          as={'span'}
          variant={'headline-7-alt'}
          translatedContent={language.noFeaturesLabel()}
        />
      )}
      <div className={'flex flex-row justify-between items-end'}>
        <S2Typography
          as={'span'}
          variant={'headline-5-alt'}
          translatedContent={language.dataRulesLabel()}
        />
        {showAddDataRuleButton && (
          <Button
            variant={'outline'}
            behavior={{
              type: 'onClick',
              onClick: () => {
                groupEntitlementCallbacks.onAddDataRule();
              },
            }}
            disabled={false}
          >
            {convertTranslatedContentToReactNode(
              language.addNewDataRuleButton(),
            )}
          </Button>
        )}
      </div>
      {dataRules.length > 0 && (
        <table className={'w-full border-gray-600 border-2 bg-white'}>
          <thead>
            <tr>
              <th
                className={
                  'bg-gray-200 border-gray-600 border-2 text-center px-8 py-4'
                }
                style={{
                  width: showDeleteDataRuleButton ? '45%' : '50%',
                }}
              >
                <S2Typography
                  as={'span'}
                  variant={'headline-7-alt'}
                  translatedContent={language.dataRuleHeaderPattern()}
                />
              </th>
              <th
                className={
                  'bg-gray-200 border-gray-600 border-2 text-center px-8 py-4'
                }
                style={{
                  width: showDeleteDataRuleButton ? '45%' : '50%',
                }}
              >
                <S2Typography
                  as={'span'}
                  variant={'headline-7-alt'}
                  translatedContent={language.dataRuleValuePattern()}
                />
              </th>
              {showDeleteDataRuleButton && (
                <th
                  className={'bg-gray-200 border-gray-600 border-2'}
                  style={{
                    width: '10%',
                  }}
                >
                  &nbsp;
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {/* eslint-disable-next-line complexity */}
            {dataRules.map((rule, index) => {
              const isCurrentlyEditingHeader =
                currentlyEditingDataRule !== null &&
                currentlyEditingDataRule.index === index &&
                currentlyEditingDataRule.part === 'header';

              const isCurrentlyEditingValue =
                currentlyEditingDataRule !== null &&
                currentlyEditingDataRule.index === index &&
                currentlyEditingDataRule.part === 'value';

              return (
                <tr key={index}>
                  <td
                    className={'p-2 align-top border-gray-600 border-2'}
                    style={{
                      width: showDeleteDataRuleButton ? '45%' : '50%',
                    }}
                  >
                    <GroupEntitlementTableColumnPart
                      part={'header'}
                      isCurrentlyEditingPart={isCurrentlyEditingHeader}
                      partPattern={rule.headerPattern}
                      index={index}
                    />
                  </td>
                  <td
                    className={'p-2 align-top border-gray-600 border-2'}
                    style={{
                      width: showDeleteDataRuleButton ? '45%' : '50%',
                    }}
                  >
                    <GroupEntitlementTableColumnPart
                      part={'value'}
                      isCurrentlyEditingPart={isCurrentlyEditingValue}
                      partPattern={rule.valuePattern}
                      index={index}
                    />
                  </td>
                  {showDeleteDataRuleButton && (
                    <td
                      className={
                        'p-2 align-top text-center border-gray-600 border-2 leading-[3.5rem]'
                      }
                      style={{
                        width: '10%',
                      }}
                    >
                      <PatternSegmentRemoveButton
                        onRemove={() => {
                          groupEntitlementCallbacks.onDeleteDataRule(index);
                        }}
                        tooltipTranslatedContent={language.removeDataRuleTooltip()}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {dataRules.length === 0 && (
        <S2Typography
          as={'span'}
          variant={'headline-7-alt'}
          translatedContent={language.noDataRulesLabel()}
        />
      )}
    </div>
  );
};
